import React, { useState } from "react";
import {
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";

// Importing icons
import PersonIcon from "../assets/icons/person.svg";
import EmailIcon from "../assets/icons/email.svg";
import PasswordIcon from "../assets/icons/password.svg";
import AccountTypeIcon from "../assets/icons/account_type.svg";
import EyeIcon from "../assets/icons/View.svg";
import EyeOffIcon from "../assets/Home/Eyeoff.svg";
import PdfModal from "components/common/PdfModal";
import IconButton from "@mui/material/IconButton";

// Styled wrapper for icons
const IconWrapper = styled("div")({
  marginRight: "8px",
  display: "flex",
  alignItems: "center",
});

// Mapping icons to their respective field names
const getIcon = (icon) => {
  switch (icon) {
    case "person":
      return <img src={PersonIcon} alt="person" />;
    case "email":
      return <img src={EmailIcon} alt="email" />;
    case "password":
      return <img src={PasswordIcon} alt="password" />;
    case "account_type":
      return <img src={AccountTypeIcon} alt="account" />;
    default:
      return null;
  }
};

// Custom InputField using MUI TextField
const InputField = ({
  type,
  name,
  value,
  onChange,
  placeholder,
  InputProps,
  select = false,
  children,
  autoComplete,
}) => (
  <TextField
    select={select}
    type={type}
    name={name}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    autoComplete={autoComplete}
    variant="outlined"
    fullWidth
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          {InputProps?.startAdornment}
        </InputAdornment>
      ),
      endAdornment: InputProps?.endAdornment ? (
        <InputAdornment position="end">
          {InputProps?.endAdornment}
        </InputAdornment>
      ) : null,
      sx: {
        "& .MuiOutlinedInput-input": {
          padding: "7px 0px", // or '0px' if you want tight spacing
        },
      },
    }}
    sx={{ borderRadius: "12px" }}
  >
    {children}
  </TextField>
);

const ReusableForm = ({
  title,
  description,
  fields,
  handleClick,
  submitButtonText,
  forgotPass = false,
  showTerms = false,
  children,
  // New optional class hooks for better layout control
  className = "",
  formClassName = "",
  initialValues: initialValuesProp,
  loading = false,
}) => {
  const navigate = useNavigate();
  // PDF modal state
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfTitle, setPdfTitle] = useState("");
  const [pdfUrl, setPdfUrl] = useState(undefined);
  // State for password visibility toggling, keyed by field name
  const [showPasswords, setShowPasswords] = useState({});

  const togglePasswordVisibility = (fieldName) => {
    setShowPasswords((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  const openPdf = async (type) => {
    try {
      if (type === "tos") {
        setPdfTitle("Terms of Service");
        const mod = await import("assets/Properteasy_TOS.pdf");
        setPdfUrl(mod?.default || mod);
      } else if (type === "tac") {
        setPdfTitle("Terms and Conditions");
        const mod = await import("assets/Properteasy_TOS.pdf");
        setPdfUrl(mod?.default || mod);
      } else {
        setPdfTitle("Privacy Policy");
        const mod = await import("assets/Properteasy_Privacy_Policy.pdf");
        setPdfUrl(mod?.default || mod);
      }
    } catch (e) {
      console.error("Failed to load PDF", e);
      setPdfUrl(undefined); // PdfModal will show a friendly message
    } finally {
      setPdfOpen(true);
    }
  };

  const closePdf = () => setPdfOpen(false);

  const initialValues = initialValuesProp || fields.reduce((acc, field) => ({
    ...acc,
    [field.name]: "",
  }), {});

  const validationSchema = Yup.object().shape(
    fields.reduce((acc, field) => {
      if (field.validation) {
        acc[field.name] = field.validation;
      }
      return acc;
    }, {})
  );

  return (
    <div className={`flex flex-col gap-2 md:gap-6 w-full p-6 ${className}`}>
      <div className="flex flex-col gap-2 justify-center items-center ">
        <h2 className="text-[#242424] font-semibold">{title}</h2>
        <h4 className="text-[#595959] text-center">{description}</h4>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, actions) => {
          // Filter values to only include those from fields array
          const filteredValues = fields.reduce((acc, field) => {
            acc[field.name] = values[field.name];
            return acc;
          }, {});

          handleClick(filteredValues, actions);
        }}
      >
        {({ values, errors, touched, handleChange, setFieldValue, isSubmitting }) => (
          <Form className={`flex flex-col gap-6 ${formClassName}`}>
            {fields.map((field) => (
              <div key={field.name}>
                <h6 className="font-semibold text-[#333333] text-[12px]">
                  {field.label}
                </h6>

                {field.type === "radio" ? (
                  <RadioGroup
                    name={field.name}
                    value={values[field.name]}
                    onChange={handleChange}
                    row
                  >
                    {field.options.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={option.label}
                      />
                    ))}
                  </RadioGroup>
                ) : field.type === "select" ? (
                  <InputField
                    select
                    name={field.name}
                    value={values[field.name]}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    autoComplete={field.autoComplete}
                    InputProps={{
                      startAdornment: (
                        <IconWrapper>{getIcon(field.icon)}</IconWrapper>
                      ),
                    }}
                  >
                    {field.placeholder && (
                      <MenuItem value="" disabled>
                        {field.placeholder}
                      </MenuItem>
                    )}
                    {field.options.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </InputField>
                ) : (
                  <InputField
                    type={
                      field.type === "password"
                        ? showPasswords[field.name]
                          ? "text"
                          : "password"
                        : field.type
                    }
                    name={field.name}
                    value={values[field.name]}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    autoComplete={field.autoComplete}
                    InputProps={{
                      startAdornment: (
                        <IconWrapper>{getIcon(field.icon)}</IconWrapper>
                      ),
                      endAdornment: field.type === "password" && (
                        <IconButton
                          type="button"
                          aria-label="toggle password visibility"
                          onClick={() => togglePasswordVisibility(field.name)}
                          edge="end"
                          size="small"
                        >
                          <img
                            src={
                              showPasswords[field.name] ? EyeOffIcon : EyeIcon
                            }
                            alt={
                              showPasswords[field.name] ? "hide" : "show"
                            }
                            style={{ width: "20px", height: "20px" }}
                          />
                        </IconButton>
                      ),
                    }}
                  />
                )}

                {touched[field.name] && errors[field.name] && (
                  <p className="text-red-500 text-[12px] mt-1">
                    {errors[field.name]}
                  </p>
                )}

                {forgotPass && field.type === "password" && (
                  <h5
                    onClick={() => navigate("/forgot-password")}
                    className="text-[#595959] cursor-pointer text-end mt-2 underline"
                  >
                    Forgot Password?
                  </h5>
                )}
              </div>
            ))}

            {showTerms && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.terms}
                      onChange={(e) => setFieldValue("terms", e.target.checked)}
                      name="terms"
                    />
                  }
                  label={
                    <p className="text-[14px] text-[#767676] font-medium">
                      By signing up, you agree to our{" "}
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          openPdf("tos");
                        }}
                        className="text-black underline cursor-pointer"
                      >
                        Terms of Service
                      </a>
                      ,{" "}
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          openPdf("privacy");
                        }}
                        className="text-black underline cursor-pointer"
                      >
                        Privacy Policy
                      </a>
                      , and{" "}
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          openPdf("tac");
                        }}
                        className="text-black underline cursor-pointer"
                      >
                        Terms and Conditions
                      </a>
                      .
                    </p>
                  }
                />
                {touched.terms && errors.terms && (
                  <p className="text-red-500 text-[12px] mt-1">
                    {errors.terms}
                  </p>
                )}
              </>
            )}

            <Button
              type="submit"
              variant="contained"
              disabled={loading || isSubmitting}
              sx={{
                backgroundColor: "#3CAB5C",
                width: "100%",
                py: 1.5,
              }}
            >
              {submitButtonText}
            </Button>

            {children}
          </Form>
        )}
      </Formik>

      {/* PDF Viewer Modal */}
      <PdfModal open={pdfOpen} onClose={closePdf} title={pdfTitle} fileUrl={pdfUrl} />
    </div>
  );
};

export default ReusableForm;
